import type { BrandConfig, LegalConfig } from '@kitup/brand';
import type { Dictionary as LocaleDictionary } from './ru.js';

export type { Dictionary } from './ru.js';

export interface LocaleConfig {
  readonly dictionary: LocaleDictionary;
  readonly dateLocale: string;
  readonly htmlLang: string;
}

/** Every locale the product supports at all. */
export const LOCALES = ['ru', 'en'] as const;
export type Locale = (typeof LOCALES)[number];

/** Locale metadata, independent of the brand. */
const LOCALE_META: Readonly<Record<Locale, { dateLocale: string; htmlLang: string }>> = {
  ru: { dateLocale: 'ru-RU', htmlLang: 'ru' },
  en: { dateLocale: 'en-US', htmlLang: 'en' },
};

/**
 * Dictionary factories brought by the app entry point.
 *
 * This module deliberately does not import them: a static table of every
 * dictionary would land in the bundle whole, and the English-language brand
 * would ship the Russian dictionary with it. The app imports exactly the ones
 * its brand declares, and the bundler drops the rest.
 */
export type DictionaryBuilders = Readonly<
  Partial<Record<Locale, (brand: BrandConfig, legal: LegalConfig) => LocaleDictionary>>
>;

export type LocaleRegistry = Readonly<Record<Locale, LocaleConfig>>;

export function isKnownLocale(value: string): value is Locale {
  return LOCALES.some((locale) => locale === value);
}

/**
 * `Object.fromEntries` is typed with an index signature, so the key union has
 * to be restored. This is the one place the cast is unavoidable, and it is
 * safe: every key was validated against `LOCALES` by the caller.
 */
function fromLocaleEntries(entries: readonly (readonly [Locale, LocaleConfig])[]): LocaleRegistry {
  return Object.fromEntries(entries) as LocaleRegistry;
}

/**
 * The brand's dictionaries. Built once per app: there is one brand per build,
 * so rebuilding them on every render is pointless.
 *
 * A locale the brand declared but the app did not bring is a configuration
 * error: better to learn about it at startup than to show the user an
 * empty UI.
 */
export function createLocales(
  brand: BrandConfig,
  legal: LegalConfig,
  builders: DictionaryBuilders,
): LocaleRegistry {
  const entries = brand.locales.map((locale) => {
    if (!isKnownLocale(locale)) {
      throw new Error(`Brand ${brand.id} declared locale "${locale}", which the product has no`);
    }
    const build = builders[locale];
    if (!build) {
      throw new Error(
        `Brand ${brand.id} declared locale "${locale}", but the app did not bring its dictionary`,
      );
    }
    return [locale, { dictionary: build(brand, legal), ...LOCALE_META[locale] }] as const;
  });
  return fromLocaleEntries(entries);
}

/** The brand's default locale, validated against its own list. */
export function defaultLocaleOf(brand: BrandConfig): Locale {
  const fallback = brand.defaultLocale;
  if (!isKnownLocale(fallback) || !brand.locales.includes(fallback)) {
    throw new Error(`Brand ${brand.id}: default locale "${fallback}" is not among its locales`);
  }
  return fallback;
}

export function resolveLocale(
  locales: LocaleRegistry,
  fallback: Locale,
  value?: string | null,
): Locale {
  return value && isKnownLocale(value) && Object.hasOwn(locales, value) ? value : fallback;
}
